import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Apple, Plus, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Horizontal scroll-strip of the user's pantry foods.
 *
 * Two modes:
 *   - smart=false (default): most-used → recent. Plain GET /custom-foods.
 *   - smart=true: hits /pantry/suggestions which ranks items by time-of-day
 *     and meal-type fit using the last 60 days of food_logs. Each chip then
 *     shows a tiny "reason" subtitle (e.g. "5× zu dieser Tageszeit").
 *
 * Tapping a chip directly logs the food at its default serving (server-
 * supplied `serving_quantity_g`, falls back to 100 g) for the currently
 * selected meal type — no extra modal, no extra confirmation.
 *
 * Props:
 *   - mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack' (default 'snack')
 *   - limit?: how many chips to render (default 10)
 *   - compact?: smaller variant for the Dashboard
 *   - smart?: enable context-aware ranking (default false)
 *   - onLogged?: optional callback fired after a successful log
 *   - title?: optional custom heading; null hides the heading
 */
export function PantryQuickPicks({ mealType = 'snack', limit = 10, compact = false, smart = false, onLogged, title }) {
    const { language } = useLanguage();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loggingId, setLoggingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            let url, res;
            if (smart) {
                const hour = new Date().getUTCHours();
                url = `${API}/api/nutrition/pantry/suggestions?limit=${limit}&meal_type=${mealType}&hour=${hour}`;
                res = await axios.get(url);
                setItems(res?.data?.suggestions || []);
            } else {
                url = `${API}/api/nutrition/custom-foods?limit=${limit}`;
                res = await axios.get(url);
                setItems(res?.data?.foods || []);
            }
        } catch (_e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [limit, smart, mealType]);

    useEffect(() => { load(); }, [load]);

    const handleQuickLog = async (item) => {
        if (loggingId) return;
        setLoggingId(item.id);
        try {
            const grams = item.serving_quantity_g || 100;
            const factor = grams / 100;
            const calories = Math.round((item.kcal_100g || 0) * factor);
            const protein = Math.round((item.protein_100g || 0) * factor * 10) / 10;
            const carbs = Math.round((item.carbs_100g || 0) * factor * 10) / 10;
            const fat = Math.round((item.fat_100g || 0) * factor * 10) / 10;
            const label = item.brand ? `${item.brand} ${item.name}` : item.name;
            await axios.post(`${API}/api/nutrition/food`, {
                name: `${label} (${grams}g)`,
                calories, protein, carbs, fat,
                meal_type: mealType,
            });
            // Bump scan_count locally so the chip moves up next time
            await axios.post(`${API}/api/nutrition/custom-food`, {
                ean: item.ean,
                name: item.name, brand: item.brand,
                kcal_100g: item.kcal_100g, protein_100g: item.protein_100g,
                carbs_100g: item.carbs_100g, fat_100g: item.fat_100g,
                serving_quantity_g: item.serving_quantity_g,
                image_url: item.image_url,
                source: item.source,
            }).catch(() => {});
            toast.success(language === 'de'
                ? `+${calories} kcal · ${label.slice(0, 28)}`
                : `+${calories} kcal · ${label.slice(0, 28)}`);
            onLogged?.();
            // Refresh list so most-used reorder is visible immediately
            load();
        } catch (_e) {
            toast.error(language === 'de' ? 'Schnelles Loggen fehlgeschlagen' : 'Quick log failed');
        } finally {
            setLoggingId(null);
        }
    };

    if (loading) {
        return (
            <div className={`flex items-center gap-2 text-xs text-muted-foreground ${compact ? 'py-2' : 'py-3'}`} data-testid="pantry-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {language === 'de' ? 'Lade Pantry…' : 'Loading pantry…'}
            </div>
        );
    }
    if (items.length === 0) return null;

    return (
        <div className="space-y-2" data-testid="pantry-strip">
            {title !== null && (
                <div className="flex items-center gap-1.5">
                    {smart ? (
                        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                    ) : (
                        <Apple className="w-3.5 h-3.5 text-primary" />
                    )}
                    <h4 className={`font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>
                        {title ?? (smart
                            ? (language === 'de' ? 'Für dich passend' : 'Smart picks for you')
                            : (language === 'de' ? 'Meine Pantry' : 'My pantry'))}
                    </h4>
                    <span className="text-[10px] text-muted-foreground">
                        {language === 'de' ? '· tippen zum Loggen' : '· tap to log'}
                    </span>
                </div>
            )}
            <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-thin">
                {items.map((item) => {
                    const isLogging = loggingId === item.id;
                    const grams = item.serving_quantity_g || 100;
                    const kcal = Math.round(((item.kcal_100g || 0) * grams) / 100);
                    const routineDue = !!item.routine_due;
                    const labHit = !routineDue && !!item.lab_marker_hit;
                    return (
                        <button
                            key={item.id}
                            onClick={() => handleQuickLog(item)}
                            disabled={isLogging}
                            data-testid={`pantry-chip-${item.id}`}
                            data-routine-due={routineDue ? 'true' : 'false'}
                            data-lab-hit={item.lab_marker_hit || ''}
                            className={`shrink-0 flex items-center gap-2 ${compact ? 'pl-1 pr-3 py-1' : 'pl-1.5 pr-3 py-1.5'} rounded-full border ${routineDue ? 'border-amber-400 ring-2 ring-amber-300/60 animate-pulse bg-amber-50/60 dark:bg-amber-900/20' : labHit ? 'border-purple-400 ring-1 ring-purple-300/50 bg-purple-50/40 dark:bg-purple-900/15' : 'border-border bg-card'} hover:border-primary/50 hover:bg-primary/5 transition-colors disabled:opacity-50 active:scale-95 relative`}
                        >
                            {routineDue && (
                                <span
                                    aria-hidden="true"
                                    className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-card"
                                    data-testid={`pantry-chip-${item.id}-routine-dot`}
                                />
                            )}
                            {labHit && (
                                <span
                                    aria-hidden="true"
                                    className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-purple-500 ring-2 ring-card"
                                    data-testid={`pantry-chip-${item.id}-lab-dot`}
                                />
                            )}
                            {item.image_url ? (
                                <img src={item.image_url} alt="" className={`${compact ? 'w-6 h-6' : 'w-7 h-7'} rounded-full object-cover bg-muted`} />
                            ) : (
                                <div className={`${compact ? 'w-6 h-6' : 'w-7 h-7'} rounded-full bg-primary/10 text-primary flex items-center justify-center`}>
                                    <Plus className="w-3 h-3" />
                                </div>
                            )}
                            <div className="text-left min-w-0">
                                <p className={`font-medium leading-tight truncate max-w-[120px] ${compact ? 'text-[11px]' : 'text-xs'}`}>{item.name}</p>
                                <p className="text-[10px] text-muted-foreground leading-tight">
                                    {kcal} kcal · {grams}g
                                </p>
                                {smart && item.reason && (
                                    <p className={`text-[9px] leading-tight truncate max-w-[140px] mt-0.5 ${routineDue ? 'text-amber-700 dark:text-amber-300 font-semibold' : labHit ? 'text-purple-700 dark:text-purple-300 font-semibold' : 'text-amber-600 dark:text-amber-400'}`}>
                                        {item.reason}
                                    </p>
                                )}
                            </div>
                            {isLogging && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
